import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import { HKPImage } from "../../media/HKPImage";
import { pickProductListThumbUrl } from "./previewMedia";
import { SalesScanStage, useSalesScanner } from "../sales/SalesScanPad";
import {
  completeIdentityMutation,
  identityError,
  identityMutationKey,
  identityRequest,
} from "../../services/physicalIdentityApi";

type ProductRef = {
  name?: string;
  productUniqueId?: string;
  thumbnailDetails?: { media?: unknown[] };
};
type Stock = {
  available?: boolean;
  serial?: string;
  carrierType?: string;
  environment?: string;
  holder?: string;
  token?: string;
  reason?: string;
};
type Existing = {
  identityStatus?: string;
  resolverPath?: string;
  carrierType?: string;
  serial?: string;
};

export default function ProductQrBindingPanel({
  product,
  open,
  onClose,
  onSaved,
}: {
  product: ProductRef | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [existing, setExisting] = useState<Existing>();
  const [query, setQuery] = useState("");
  const [stock, setStock] = useState<Stock>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const generation = useRef(0);
  const lookupGeneration = useRef(0);
  const stopCamera = useRef<() => void>(() => undefined);
  const productId = product?.productUniqueId || "";
  const thumb = pickProductListThumbUrl(product?.thumbnailDetails?.media);

  const lookup = async (raw: string) => {
    const value = raw.trim();
    if (!value) { setError("Enter the printed serial, the code beside the QR, or the full label link."); return; }
    const n = ++lookupGeneration.current;
    setBusy(true); setError(""); setStock(undefined); setConfirm(false);
    try {
      const result = await identityRequest<Stock>(`/qr-stock?q=${encodeURIComponent(value)}`);
      if (lookupGeneration.current === n) setStock(result);
    } catch (e) { if (lookupGeneration.current === n) setError(identityError(e)); }
    finally { if (lookupGeneration.current === n) setBusy(false); }
  };
  const scan = useSalesScanner((raw) => { setQuery(raw); return lookup(raw); }, busy || !open);
  stopCamera.current = scan.stopCamera;

  useEffect(() => {
    if (open) return;
    stopCamera.current();
  }, [open]);

  useEffect(() => {
    if (!open || !productId) return;
    const n = ++generation.current;
    lookupGeneration.current++;
    setQuery(""); setStock(undefined); setError(""); setConfirm(false); setExisting(undefined); setBusy(true);
    identityRequest<Existing>(`/entities/PRODUCT/${encodeURIComponent(productId)}`)
      .then(row => { if (generation.current === n) setExisting(row); })
      .catch(e => { if (generation.current === n) setError(identityError(e)); })
      .finally(() => { if (generation.current === n) setBusy(false); });
    return () => { generation.current++; lookupGeneration.current++; stopCamera.current(); };
  }, [open, productId]);

  const attach = async () => {
    if (!stock?.token || !productId) return;
    const n = generation.current;
    setBusy(true); setError("");
    const path = `/${stock.token}/bind`;
    const body = { entityType: "PRODUCT", entityId: productId };
    try {
      await identityRequest(path, body, identityMutationKey(path, body));
      completeIdentityMutation();
      if (generation.current !== n) return;
      setConfirm(false); onSaved(); onClose();
    } catch (e) { if (generation.current === n) setError(identityError(e)); }
    finally { if (generation.current === n) setBusy(false); }
  };

  const managedNfc = existing?.identityStatus === "ACTIVE" && existing.carrierType === "NFC";
  const attachedQr = existing?.identityStatus === "ACTIVE" && existing.carrierType !== "NFC" && existing.resolverPath;
  return <Modal isOpen={open} toggle={onClose} centered>
    <ModalHeader toggle={onClose}>Add QR</ModalHeader>
    <ModalBody>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        {thumb ? <HKPImage preset="THUMBNAIL" legacyUrl={thumb} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }} /> : null}
        <div>
          <strong>{product?.name || "Product"}</strong>
          <p style={{ margin: 0 }}>{productId}</p>
          <p style={{ margin: 0 }}>QR label</p>
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      {managedNfc && <p>This product already has a managed NFC identity. Adding a QR alongside it is not supported in this phase.</p>}
      {attachedQr && <p>This product already has a QR. <a href={existing.resolverPath}>View QR</a>{existing.serial ? ` · ${existing.serial}` : ""}</p>}
      {!managedNfc && !attachedQr && <>
        <Label for="qr-lookup">Printed serial, code, or label link</Label>
        <Input id="qr-lookup" value={query} onChange={e => { lookupGeneration.current++; setQuery(e.target.value); setStock(undefined); setConfirm(false); setBusy(false); }} onKeyDown={e => { if (e.key === "Enter") void lookup(query); }} autoComplete="off" />
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Button className="btn-hastkala" disabled={busy} onClick={() => void lookup(query)}>Check label</Button>
          <Button outline disabled={busy} onClick={scan.startCamera}>Scan QR</Button>
          <Button outline disabled={busy} onClick={() => scan.fileRef.current?.click()}>Upload QR</Button>
        </div>
        <input ref={scan.fileRef} type="file" accept="image/*" hidden onChange={e => { scan.onFile(e.target.files?.[0]); e.target.value = ""; }} />
        <SalesScanStage scan={scan} />
        {stock && <section style={{ marginTop: 12 }}>
          <p>{stock.available ? "Ready to attach" : stock.reason}</p>
          {stock.serial && <p>Serial: {stock.serial}</p>}
          {stock.carrierType && <p>Type: {stock.carrierType}{stock.environment ? ` · ${stock.environment}` : ""}</p>}
          {stock.holder && <p>Holder: {stock.holder}</p>}
          {stock.available && !confirm && <Button disabled={busy} onClick={() => setConfirm(true)}>Review attachment</Button>}
          {confirm && <>
            <p>Attach this physical QR label to {product?.name || "this product"}? Check the label is on the correct piece.</p>
            <Button disabled={busy} onClick={() => void attach()}>{busy ? "Saving…" : "Attach label"}</Button>
          </>}
        </section>}
      </>}
    </ModalBody>
  </Modal>;
}
